import {
  createMachine,
  assign,
  fromCallback,
  fromPromise,
  raise,
} from "xstate";
import { UploadContext, Image } from "./config/types";

import { createImages, s3ImageUploader } from "./config/services";
import {
  prepareImages,
  clearHandles,
  applyIds,
  completeUpload,
  updateProgress,
  removeImages,
} from "./config/actions";

type UploadEvent =
  | { type: "PREPARE"; files: File[] }
  | { type: "AUTO_COMPLETE"; autoComplete: boolean }
  | { type: "UPLOAD" }
  | { type: "REMOVE"; uids: string[] }
  | { type: "START"; total: number }
  | { type: "UPDATE"; total: number }
  | { type: "FILE_COMPLETE"; id: string; stats: unknown }
  | { type: "FILE_PROGRESS"; id: string; progress: number; stats: unknown }
  | { type: "REGISTER_CANCEL"; cancel: () => void }
  | { type: "CANCEL" }
  | { type: "COMPLETE" }
  | { type: "INCOMPLETE" };

const getAuth = () =>
  fetch("/api/v1/auth/aws").then((response) => response.json());

const createImagesRequest = (
  surveyId: number,
  orderId: number,
  images: Image[]
) =>
  fetch(`/api/v1/surveys/${surveyId}/images`, {
    method: "POST",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      images,
      orderId,
    }),
  }).then((response) => response.json());

const machine = createMachine(
  {
    types: {} as {
      context: UploadContext;
      events: UploadEvent;
    },
    id: "uploader",
    initial: "idle",
    context: {
      surveyId: null,
      orderId: null,
      files: [],
      images: {},
      progress: {},
      cancel: null,
      autoComplete: true,
    },
    states: {
      idle: {
        initial: "fresh",
        on: {
          PREPARE: {
            target: "adding",
            actions: assign(({ event }) => ({
              files: event.files,
            })),
          },
          AUTO_COMPLETE: {
            actions: assign(({ event }) => ({
              autoComplete: event.autoComplete,
            })),
          },
          UPLOAD: "registering",
          REMOVE: [
            {
              actions: "removeImages",
              target: ".fresh.deleted",
              guard: "isRemovingAll",
            },
            {
              actions: "removeImages",
              target: ".fresh.deleted",
              guard: "isRemainingUploaded",
            },
            {
              actions: "removeImages",
              target: ".pending",
            },
          ],
        },
        states: {
          fresh: {
            initial: "waiting",
            states: {
              waiting: {},
              deleted: {},
            },
          },
          pending: {},
        },
      },
      adding: {
        invoke: {
          id: "createImages",
          src: "createImages",
          input: ({ context, event }) => ({ context, event }),
          onDone: [
            {
              target: "idle.pending",
              actions: "prepareImages",
              guard: "isImages",
            },
            { target: "#uploader.registering", actions: "prepareImages" },
          ],
        },
        on: {
          START: {
            actions: assign(({ event }) => ({
              adding: {
                total: event.total,
                added: 0,
              },
            })),
          },
          UPDATE: {
            actions: assign(({ context, event }) => ({
              adding: {
                total: context.adding?.total ?? 0,
                added: (context.adding?.added ?? 0) + event.total,
              },
            })),
          },
        },
      },
      registering: {
        invoke: {
          id: "registerImages",
          src: "registerImages",
          input: ({ context }) => ({ context }),
          onDone: {
            target: "authenticating",
            actions: "applyIds",
          },
          onError: {
            target: "failure",
          },
        },
      },
      authenticating: {
        invoke: {
          id: "retrieveAuthorization",
          src: "retrieveAuthorization",
          onDone: {
            target: "uploading",
          },
        },
      },
      uploading: {
        initial: "progress",
        invoke: {
          id: "createUploader",
          src: "browserUploader",
          input: ({ context, event }) => ({ context, event }),
          onError: "failure",
        },
        on: {
          FILE_COMPLETE: {
            target: ".checkall",
            actions: "completeUpload",
          },
          FILE_PROGRESS: {
            actions: "updateProgress",
          },
          REGISTER_CANCEL: {
            actions: assign(({ event }) => ({
              cancel: event.cancel,
            })),
          },
          CANCEL: ".cancel",
          COMPLETE: "complete",
          INCOMPLETE: "idle",
          ERROR: "failure",
        },
        states: {
          checkall: {
            always: [
              {
                target: "complete",
                actions: "clearHandles",
                guard: "isBatchAutoComplete",
              },
              {
                target: "idle",
                actions: "clearHandles",
                guard: "isBatchComplete",
              },
              {
                target: "progress",
              },
            ],
          },
          progress: {},
          idle: {
            entry: raise({ type: "INCOMPLETE" }),
          },
          complete: {
            entry: raise({ type: "COMPLETE" }),
          },
          cancel: {
            invoke: {
              src: "cancelUpload",
              input: ({ context }) => ({ context }),
              onDone: {
                target: "#uploader.cancelled",
              },
            },
          },
        },
      },
      failure: {},
      complete: {
        type: "final",
        output: ({ context }) => ({ images: context.images }),
      },
      cancelled: {},
    },
  },
  {
    guards: {
      isComplete: ({ context }) => {
        return (
          context.files.length === 0 &&
          Object.values(context.images).every(
            (image) => image.uploaded === true
          )
        );
      },
      isMissing: ({ context }) => {
        return (
          Object.values(context.images).some(
            (image: any) => image.id !== null && image.uploaded === false
          ) && Object.keys(context.progress).length === 0
        );
      },
      isBatchAutoComplete: ({ context }) => {
        return (
          Object.values(context.progress).filter(
            (file: { progress: number; stats: unknown }) => file.progress < 1
          ).length === 0 && context.autoComplete === true
        );
      },
      isBatchComplete: ({ context }) => {
        return (
          Object.values(context.progress).filter(
            (file: { progress: number; stats: unknown }) => file.progress < 1
          ).length === 0
        );
      },
      isRemovingAll: ({ context, event }) =>
        Object.keys(context.images).length === event.uids.length,
      isRemainingUploaded: ({ context, event }) => {
        return Object.values(context.images)
          .filter((image: any) => event.uids.includes(image.uid) === false)
          .every((image: any) => image.uploaded === true);
      },
    },
    actions: {
      clearHandles,
      applyIds,
      completeUpload,
      updateProgress,
      removeImages,
    },
    actors: {
      cancelUpload: fromPromise(({ input }) => {
        const ctx = (input as { context: UploadContext }).context;
        if (ctx.cancel) {
          ctx.cancel();
        }
        return Promise.resolve();
      }),
      registerImages: fromPromise(({ input }) => {
        const ctx = (input as { context: UploadContext }).context;
        const allImages = Object.values(ctx.images)
          .filter(
            (image: Image) =>
              (image.id === undefined || image.id === null) &&
              image.uploaded === false
          )
          .map(({ handle, extension, ...allowed }) => ({ ...allowed }));

        /*
          we have a maximum body size, but this can still go over with all of this exif data
          this will control the amount of data sent on every request  
        */
        const batchSize = 100;
        const batchCount = Math.floor(allImages.length / batchSize);
        const batches = [] as unknown[];

        for (let i = 0; i < batchCount; i++) {
          batches.push(
            allImages.slice(i * batchSize, i * batchSize + batchSize)
          );
        }

        batches.push(
          allImages.slice(
            batchSize * batchCount,
            batchSize * batchCount + (allImages.length % batchSize)
          )
        );

        return batches.reduce(
          (p, batch) =>
            p.then((values: unknown[]) =>
              createImagesRequest(ctx.surveyId, ctx.orderId, batch).then(
                (created) => {
                  return [...values, ...created];
                }
              )
            ),
          Promise.resolve([] as unknown[])
        );
      }),
      retrieveAuthorization: fromPromise(() => getAuth()),
    },
  }
);

export const imageUploaderMachine = machine.provide({
  guards: {
    isImages: () => true,
  },
  actions: {
    prepareImages,
  },
  actors: {
    createImages: fromCallback(({ input, sendBack }) => {
      createImages(
        (input as any).context,
        (input as any).event
      )((evt) => sendBack(evt)).then((output) => {
        sendBack({ type: "xstate.done.actor", output });
      });
    }),
    browserUploader: fromCallback(({ input, sendBack }) => {
      s3ImageUploader(
        (input as any).context,
        (input as any).event
      )((evt) => sendBack(evt)).then(() => {
        // optional done
      });
    }),
  },
});

export default machine;
